import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Brain, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, XCircle, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LearningInsightsPanel = ({ gameId }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (gameId) {
      fetchInsights();
    }
  }, [gameId]);

  const fetchInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/games/${gameId}/learning-insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(response.data);
    } catch (err) {
      console.error('Failed to load learning insights:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-purple-500/5 to-purple-600/10 border-purple-500/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-400"></div>
            <span className="text-sm text-muted-foreground">Loading AI insights...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!insights) return null;

  const { insight_summary, adjustments, direct_matchup_history, home_team_learnings, away_team_learnings, home_team, away_team } = insights;

  // Safe access with defaults
  const safeAdjustments = adjustments || { home_boost: 0, away_boost: 0, reasons: [] };
  const safeHomeLearnings = home_team_learnings || { accuracy: 0, correct_predictions: 0, total_games: 0 };
  const safeAwayLearnings = away_team_learnings || { accuracy: 0, correct_predictions: 0, total_games: 0 };
  
  const hasAdjustments = safeAdjustments.home_boost > 0 || safeAdjustments.away_boost > 0;

  return (
    <Card className="bg-gradient-to-br from-purple-500/5 to-purple-600/10 border-purple-500/20" data-testid="learning-insights-panel">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Brain className="w-5 h-5 text-purple-400" />
          AI Learning Insights
          {hasAdjustments && (
            <Badge variant="outline" className="ml-2 bg-purple-500/10 text-purple-400 border-purple-500/30">
              Adjustments Applied
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Text */}
        <div className="p-3 bg-card/50 rounded-lg border border-border/50">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">{insight_summary}</p>
          </div>
        </div>

        {/* Adjustments */}
        {hasAdjustments && (
          <div className="grid grid-cols-2 gap-3">
            {adjustments.home_boost > 0 && (
              <div className="flex items-center gap-2 p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <div>
                  <p className="text-xs text-muted-foreground">{home_team}</p>
                  <p className="text-sm font-semibold text-green-400">+{adjustments.home_boost}%</p>
                </div>
              </div>
            )}
            {adjustments.away_boost > 0 && (
              <div className="flex items-center gap-2 p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <div>
                  <p className="text-xs text-muted-foreground">{away_team}</p>
                  <p className="text-sm font-semibold text-blue-400">+{adjustments.away_boost}%</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Adjustment Reasons */}
        {adjustments.reasons?.length > 0 && (
          <div className="space-y-1">
            {adjustments.reasons.slice(0, expanded ? undefined : 2).map((reason, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-amber-400">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                <span>{reason}</span>
              </div>
            ))}
            {adjustments.reasons.length > 2 && !expanded && (
              <button 
                onClick={() => setExpanded(true)}
                className="text-xs text-purple-400 hover:underline"
              >
                +{adjustments.reasons.length - 2} more insights
              </button>
            )}
          </div>
        )}

        {/* Direct Matchup History */}
        {direct_matchup_history?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Head-to-Head History</p>
            <div className="space-y-2">
              {direct_matchup_history.slice(0, expanded ? 5 : 2).map((match, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-card/30 rounded border border-border/30">
                  <div className="flex items-center gap-2">
                    {match.prediction_correct ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className="text-xs">{match.away_team} @ {match.home_team}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground">{match.date}</span>
                    {match.score_diff && (
                      <span className={`ml-2 text-xs ${match.score_diff <= 10 ? 'text-green-400' : 'text-amber-400'}`}>
                        ±{match.score_diff}pts
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Learning Stats */}
        {expanded && (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/30">
            <div className="text-center p-2 bg-card/30 rounded">
              <p className="text-xs text-muted-foreground">{home_team || 'Home'} Predictions</p>
              <p className="text-lg font-bold">
                {safeHomeLearnings.accuracy}%
              </p>
              <p className="text-xs text-muted-foreground">
                {safeHomeLearnings.correct_predictions}/{safeHomeLearnings.total_games}
              </p>
            </div>
            <div className="text-center p-2 bg-card/30 rounded">
              <p className="text-xs text-muted-foreground">{away_team || 'Away'} Predictions</p>
              <p className="text-lg font-bold">
                {safeAwayLearnings.accuracy}%
              </p>
              <p className="text-xs text-muted-foreground">
                {safeAwayLearnings.correct_predictions}/{safeAwayLearnings.total_games}
              </p>
            </div>
          </div>
        )}

        {/* Recent Form - Always Show */}
        {insights?.recent_form && (
          <div className="pt-2 border-t border-border/30">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Recent Form (Last 5 Games)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-2 bg-card/30 rounded border border-border/30">
                <div className="w-3 h-3 bg-green-500/30 rounded-full" />
                <div>
                  <p className="text-xs text-muted-foreground">{insights.recent_form.home_team?.name}</p>
                  <p className="text-sm font-semibold">{insights.recent_form.home_team?.last_5_record || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-card/30 rounded border border-border/30">
                <div className="w-3 h-3 bg-blue-500/30 rounded-full" />
                <div>
                  <p className="text-xs text-muted-foreground">{insights.recent_form.away_team?.name}</p>
                  <p className="text-sm font-semibold">{insights.recent_form.away_team?.last_5_record || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        {(direct_matchup_history?.length > 2 || adjustments.reasons?.length > 2) && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="w-full text-center text-xs text-purple-400 hover:text-purple-300 py-1"
          >
            {expanded ? 'Show Less' : 'Show More Details'}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

export default LearningInsightsPanel;
